import { AccountInfo } from "@azure/msal-browser";
import { useEffect, useState } from "react";
import { API_CLIENTS_BACKEND, API_USERS_BACKEND } from "../api/api";
import { fetchUserResponse, fetchUserResponseByServiceProvider } from "../api/fetchUserResponse";
import { fetchServiceProviderResponse } from "../api/fetchServiceProviderResponse";
import { initializeFeatureFlags } from "../context/slices/featureFlagsSlice";
import { setLoggedInClient } from "../context/slices/loggedInClientSlice";
import { setStateInUs } from "../context/slices/stateInUsSlice";
import { setUserResponse } from "../context/slices/userProfileSlice";
import { useXNGDispatch } from "../context/store";
import { placeholderForFutureLogErrorText } from "../temp/errorText";
import dayjs from "dayjs";
import type { UserResponse } from "../profile-sdk";
import { cookieExists, extractCookieValue } from "../utils/cookies";
import { setServiceProviderProfile } from "../context/slices/serviceProviderProfileSlice";

/**
 * Custom hook that populates the Redux store with user data.
 *
 * @param account - The account information of the user.
 * @param setShowSignatoryVeiw - A function to set the visibility of the signatory view.
 * @returns An object containing the state variables `reduxPopulated`, `userInactive`, and `disable_flow`.
 */
export function usePopulateReduxStore(
  account: Readonly<AccountInfo> | null,
  setShowSignatoryVeiw: React.Dispatch<React.SetStateAction<boolean>>,
) {
  const [reduxPopulated, setReduxPopulated] = useState<boolean>(false);
  const [userInactive, setUserInactive] = useState(false);
  const [disable_flow, setDisable_flow] = useState<boolean>(false);
  const dispatch = useXNGDispatch();
  useEffect(() => {
    if (account === null) return;

    async function populateReduxStore(account: Readonly<AccountInfo>) {
      setReduxPopulated(false);
      // HANDLE POPULATE REDUX STORE

      if (account.idTokenClaims === undefined) throw new Error(placeholderForFutureLogErrorText);

      const _state = account.idTokenClaims.state as string;
      const _oid = account.idTokenClaims.oid;
      const _email = account.idTokenClaims.emails![0];
      const _given_name = account.idTokenClaims.given_name as string;
      const _family_name = account.idTokenClaims.family_name as string;
      const _extension_DoB = dayjs(account.idTokenClaims.extension_DoB as string).format("YYYY-MM-DD");
      if (_oid === undefined) throw new Error(placeholderForFutureLogErrorText);

      // stateInUs is set below after we resolve the logged-in client assignment.
      // The client's ClientRef2.state is authoritative; token claim is the fallback
      // (used only when the user has no client assignments, i.e. the onboarding flow).
      let userResponse: UserResponse | null = null;
      try {
        if (cookieExists("loggedInAsUserID")) {
          const providerID = extractCookieValue("loggedInUserProviderID");
          const clientID = extractCookieValue("loggedInUserClientID");
          userResponse = await fetchUserResponseByServiceProvider(providerID, clientID);
          // prevents users from getting stuck on the electronic signature onboarding view
          // until a better method for logging in as another user is implemented
          setDisable_flow(true);
          console.log("DISABLE FLOW");
        } else {
          userResponse = await fetchUserResponse(_oid!, _email);
        }
      } catch {
        const backendUser = await API_USERS_BACKEND.postUser({
          postUserBody: {
            objectId: _oid,
            firstName: _given_name,
            lastName: _family_name,
            email: _email,
            dateOfBirth: new Date(_extension_DoB),
          },
        });
        if (!backendUser?.id) throw new Error(placeholderForFutureLogErrorText);
        userResponse = await fetchUserResponse(backendUser.id);
      }
      
      // Update last login timestamp via xng-backend
      if (userResponse?.id && !cookieExists("loggedInAsUserID")) {
        try {
          await API_USERS_BACKEND.patchLogin({ id: userResponse.id });
        } catch (error) {
          console.error("Failed to update last login timestamp:", error);
          // Don't block login if this fails
        }
      }
      
      // populate setUserResponse
      dispatch(setUserResponse(userResponse));

      if (userResponse.clientAssignments !== null) {
        if (userResponse.clientAssignments!.length > 0) {
          // set to last logged in or zeroth by default
          const activeClientIDs =
            userResponse.clientAssignments
              ?.filter((ca) => (ca as any).status === 1)
              .map((ca) => ca.client!.id) ?? [];
          const fallbackClientID =
            activeClientIDs[0] ?? userResponse.clientAssignments![0].client!.id;
          const proxyClientID = cookieExists("loggedInUserClientID")
            ? extractCookieValue("loggedInUserClientID")
            : undefined;
          const loggedInClientID =
            proxyClientID || userResponse.loggedInClientId || fallbackClientID;
          if (!loggedInClientID) throw new Error(placeholderForFutureLogErrorText);
          if (loggedInClientID === undefined) throw new Error(placeholderForFutureLogErrorText);
          const backendClient = await API_CLIENTS_BACKEND.getClient({ clientId: loggedInClientID });
          const loggedInClient = {
            id: backendClient.id,
            name: backendClient.name,
            status: backendClient.status as any,
            usesCampusRestrictions: backendClient.usesCampusRestrictions,
            fiscalAgent: backendClient.fiscalAgent as any,
            additionalDistricts: backendClient.additionalDistricts as any,
          };

          // populate setLoggedInClient
          dispatch(setLoggedInClient(loggedInClient));
          await dispatch(initializeFeatureFlags(loggedInClientID));

          const clientAssignment = userResponse?.clientAssignments?.find((ca) => {
            return ca.client?.id === loggedInClientID;
          });

          // populate state in US from the logged-in client's ClientRef2.state
          const clientState = (clientAssignment?.client as any)?.state as string | undefined;
          dispatch(setStateInUs(clientState ?? _state));

          const serviceProvider = clientAssignment?.serviceProviderProfile;

          if (serviceProvider) {
            const serviceProviderProfile = await fetchServiceProviderResponse(serviceProvider.id!);

            if (!serviceProviderProfile.clientAssignmentStatus) {
              setUserInactive(true);
            } else {
              dispatch(setServiceProviderProfile(serviceProviderProfile));
            }
          }

          if (
            userResponse.electronicSignatures &&
            (userResponse.electronicSignatures?.length < 3 ||
              !!userResponse.electronicSignatures.find((signature) => !signature.isSigned)) &&
            !!serviceProvider
          ) {
            setShowSignatoryVeiw(true);
          }
        }
      }

      // Fallback: if no client assignment set the state, use the token claim.
      // This covers users in the onboarding/registration flow who have no assignments yet.
      if (!userResponse.clientAssignments?.length) {
        dispatch(setStateInUs(_state));
      }

      // We thought that this state for Scheduler screen operation  would have to be cached on page load
      // const dropdownOptions: Service[] = [];
      // const ServicesByServiceProviderTypeResponse =
      //   API_STATESNAPSHOTS.v1StateSnapshotsByDateServicesByServiceProviderTypeGet(_state, )
      // dispatch(setServiceDropdownOptions(dropdownOptions));
      setReduxPopulated(true);
    }

    populateReduxStore(account);
  }, [account]);
  return { reduxPopulated, userInactive, disable_flow };
}